import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  Button,
  CloseIcon,
  Combobox,
  ConfirmDialog,
  DiscardChangesDialog,
  LoadingSkeleton,
  NumberInput,
  useToast,
} from "@a2z/ui";
import type { PaymentReviewRejectReasonCode, SellerOrderDetail } from "@/lib/api/types";
import { formatAmountValue } from "./order-list.utils";
import { PaymentReviewProofViewer } from "./PaymentReviewProofViewer";
import { usePaymentReviewState } from "./usePaymentReviewState";

type DecisionMode = "approve" | "reject";

type Props = {
  locale: string;
  storeSlug: string;
  orderId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (order: SellerOrderDetail) => void;
  openOrderHref?: string;
};

function mapErrorMessage(
  code: string | null,
  t: ReturnType<typeof useTranslations>,
): string | null {
  if (!code) {
    return null;
  }
  if (code === "STALE_STATUS") {
    return t(keys.orders.paymentReviewConflictNotice);
  }
  if (code === "PAYMENT_PROOF_REQUIRED") {
    return t(keys.orders.paymentReviewProofRequired);
  }
  if (code === "INVALID_APPROVED_AMOUNT") {
    return t(keys.orders.approvedAmountInvalid);
  }
  if (code === "PAYMENT_REJECT_REASON_REQUIRED") {
    return t(keys.orders.reviewRejectReasonRequired);
  }
  if (code === "PAYMENT_REVIEW_LOAD_ERROR") {
    return t(keys.orders.paymentReviewLoadError);
  }
  return t(keys.errors.generic);
}

function getDifferenceColorClass(difference: number): string {
  if (difference < 0) return "text-[color:var(--error-500)]";
  if (difference > 0) return "text-[color:var(--warning-500)]";
  return "text-[color:var(--success-500)]";
}

function getDifferenceMeta(
  difference: number,
  t: ReturnType<typeof useTranslations>,
) {
  if (difference < 0) {
    return t(keys.orders.differenceUnderpaid);
  }
  if (difference > 0) {
    return t(keys.orders.differenceOverpaid);
  }
  return t(keys.orders.differenceExact);
}

export function PaymentReviewDialog({
  locale,
  storeSlug,
  orderId,
  isOpen,
  onClose,
  onSuccess,
  openOrderHref,
}: Props) {
  const t = useTranslations();
  const toast = useToast();
  const [decisionMode, setDecisionMode] = useState<DecisionMode>("approve");
  const [lastAction, setLastAction] = useState<"approve" | "reject">("approve");
  const [showApproveSummary, setShowApproveSummary] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [approvedAmountTouched, setApprovedAmountTouched] = useState(false);
  const touchStartY = useRef<number | null>(null);

  const state = usePaymentReviewState({
    isOpen,
    storeSlug,
    orderId,
    onSuccess: (updatedOrder) => {
      toast.success(
        lastAction === "reject"
          ? t(keys.orders.paymentRejectedNotice)
          : t(keys.orders.paymentApprovedNotice),
      );
      onSuccess(updatedOrder);
      setShowApproveSummary(false);
      setShowRejectConfirm(false);
      onClose();
    },
  });

  // Reset local state on dialog close — render-time previous-value comparison
  // instead of a setState-in-effect.
  const isActive = Boolean(isOpen && orderId);
  const [prevIsActive, setPrevIsActive] = useState(isActive);
  if (isActive !== prevIsActive) {
    setPrevIsActive(isActive);
    if (!isActive) {
      setDecisionMode("approve");
      setShowApproveSummary(false);
      setShowRejectConfirm(false);
      setApprovedAmountTouched(false);
    }
  }

  const requestClose = () => {
    if (state.isSubmitting) {
      return;
    }
    if (state.hasUnsavedChanges) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  useEffect(() => {
    if (!isOpen || !orderId) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        requestClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, state.hasUnsavedChanges, state.isSubmitting]);

  const handleModeChange = (mode: DecisionMode) => {
    setDecisionMode(mode);
    setShowApproveSummary(false);
    state.setErrorCode(null);
  };

  const handleApprove = () => {
    if (!state.hasProof) {
      state.setErrorCode("PAYMENT_PROOF_REQUIRED");
      return;
    }
    if (!state.hasValidApprovedAmount) {
      state.setErrorCode("INVALID_APPROVED_AMOUNT");
      setApprovedAmountTouched(true);
      return;
    }
    state.setErrorCode(null);
    setShowApproveSummary(true);
  };

  const handleConfirmApprove = () => {
    setLastAction("approve");
    void state.submitApprove();
  };

  const handleReject = () => {
    if (!state.reasonCode) {
      state.setErrorCode("PAYMENT_REJECT_REASON_REQUIRED");
      return;
    }
    setLastAction("reject");
    state.setErrorCode(null);
    setShowRejectConfirm(true);
  };

  const rejectOptions = useMemo(
    () =>
      [
        ["amount_mismatch", keys.orders.reviewRejectAmountMismatch],
        ["proof_unclear", keys.orders.reviewRejectProofUnclear],
        ["wrong_proof", keys.orders.reviewRejectWrongProof],
        ["suspected_fake", keys.orders.reviewRejectSuspectedFake],
        ["other", keys.orders.reviewRejectOther],
      ] as Array<[PaymentReviewRejectReasonCode, string]>,
    [],
  );
  const rejectReasonLabelByCode = useMemo(
    () =>
      Object.fromEntries(rejectOptions) as Record<PaymentReviewRejectReasonCode, string>,
    [rejectOptions],
  );

  if (!isOpen || !orderId) {
    return null;
  }

  // Expected amount = the deposit due when one exists, otherwise the full
  // order total (no-deposit / full-online orders have requiredAmount 0, so
  // comparing against it would mislabel a correct full payment as overpaid).
  const expectedAmount =
    state.requiredAmount > 0
      ? state.requiredAmount
      : (state.order?.totalAmount ?? state.requiredAmount);
  const difference = state.buyerEnteredAmount - expectedAmount;
  const differenceLabel = getDifferenceMeta(difference, t);
  const differenceColorClass = getDifferenceColorClass(difference);
  const errorMessage = mapErrorMessage(state.errorCode, t);
  const approvedAmountHasError =
    approvedAmountTouched && !state.hasValidApprovedAmount && !state.isLoading;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={requestClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center md:p-4">
        <div
          role="dialog"
          aria-modal="true"
          onTouchStart={(event) => {
            touchStartY.current = event.changedTouches[0]?.clientY ?? null;
          }}
          onTouchEnd={(event) => {
            const startY = touchStartY.current;
            const endY = event.changedTouches[0]?.clientY ?? null;
            if (startY !== null && endY !== null && endY - startY > 80) {
              requestClose();
            }
            touchStartY.current = null;
          }}
          className="flex h-[92vh] w-full flex-col rounded-t-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-lg)] md:h-auto md:max-h-[90vh] md:max-w-4xl md:rounded-2xl"
        >
          {/* Drag handle — mobile only */}
          <div className="flex justify-center pt-3 md:hidden" aria-hidden="true">
            <div className="h-1 w-8 rounded-full bg-[color:var(--border-light)]" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between border-b border-[color:var(--border-light)] px-4 py-3 sm:px-5">
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-[color:var(--text-primary)]">
                {t(keys.orders.reviewPaymentTitle)}
              </h3>
              {state.order ? (
                <p className="truncate text-xs text-[color:var(--text-muted)]">
                  {state.order.customer.name} • {state.order.orderCode}
                </p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {openOrderHref ? (
                <a
                  href={openOrderHref}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[color:var(--text-muted)] hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text-primary)]"
                  title={t(keys.orders.openOrderAction)}
                >
                  <ExternalLinkIcon />
                </a>
              ) : null}
              <Button
                variant="outline"
                size="md"
                onClick={requestClose}
                aria-label={t(keys.common.close)}
                leadingIcon={<CloseIcon size={18} />}
              />

            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
            {/* Conflict banner */}
            {state.isConflict ? (
              <div className="mb-3 rounded-lg border border-[color:var(--warning-500)]/35 bg-[color:var(--warning-500)]/10 px-3 py-2 text-sm text-[color:var(--text-primary)]">
                {t(keys.orders.paymentReviewConflictNotice)}{" "}
                <span className="text-[color:var(--text-muted)]">
                  {t(keys.orders.paymentReviewOpenOrderHint)}
                </span>
              </div>
            ) : null}

            {/* Error banner */}
            {errorMessage && !state.isConflict ? (
              <div className="mb-3 rounded-lg border border-[color:var(--error-500)]/35 bg-[color:var(--error-500)]/10 px-3 py-2 text-sm text-[color:var(--error-500)]">
                {errorMessage}
              </div>
            ) : null}

            {state.isLoading ? (
              <LoadingSkeleton variant="detail" rows={3} />
            ) : state.order ? (
              <>
                {/* Segmented control — Approve / Reject mode */}
                {!state.isConflict ? (
                  <div className="mb-4 flex rounded-xl border border-[color:var(--border-light)] p-1">
                    <button
                      type="button"
                      onClick={() => handleModeChange("approve")}
                      className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${
                        decisionMode === "approve"
                          ? "bg-[color:var(--primary-50)] text-[color:var(--primary-600)]"
                          : "text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
                      }`}
                    >
                      {t(keys.orders.approveModeLabel)}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleModeChange("reject")}
                      className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${
                        decisionMode === "reject"
                          ? "bg-[color:var(--error-500)]/10 text-[color:var(--error-500)]"
                          : "text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
                      }`}
                    >
                      {t(keys.orders.rejectModeLabel)}
                    </button>
                  </div>
                ) : null}

                {/* Two-column on desktop, stacked on mobile */}
                <div className="md:grid md:grid-cols-[3fr_2fr] md:gap-5">
                  {/* Left column: proof + amounts */}
                  <div className="space-y-4">
                    {/* Payment proof section */}
                    <section>
                      <SectionHeading>{t(keys.orders.sectionPaymentProof)}</SectionHeading>
                      <PaymentReviewProofViewer
                        locale={locale}
                        proofItems={state.proofItems}
                        selectedIndex={state.selectedProofIndex}
                        onSelect={state.setSelectedProofIndex}
                      />
                    </section>

                    {/* Amount summary section */}
                    <section className="rounded-xl border border-[color:var(--border-light)] p-3 text-sm">
                      <SectionHeading className="mb-2">{t(keys.orders.sectionAmountSummary)}</SectionHeading>
                      <div className="flex items-center justify-between py-1">
                        <span className="text-[color:var(--text-muted)]">{t(keys.orders.requiredAmountLabel)}</span>
                        <bdi className="font-medium">{formatAmountValue(locale, expectedAmount)}</bdi>
                      </div>
                      <div className="flex items-center justify-between py-1">
                        <span className="text-[color:var(--text-muted)]">{t(keys.orders.buyerEnteredAmountLabel)}</span>
                        <bdi className="font-medium">{formatAmountValue(locale, state.buyerEnteredAmount)}</bdi>
                      </div>
                      <div className={`flex items-center justify-between py-1 font-semibold ${differenceColorClass}`}>
                        <span>{t(keys.orders.differenceAmountLabel)}</span>
                        <bdi>
                          {formatAmountValue(locale, difference)} ({differenceLabel})
                        </bdi>
                      </div>
                    </section>
                  </div>

                  {/* Right column: decision section */}
                  <div className="mt-4 md:mt-0">
                    <SectionHeading className="mb-3">{t(keys.orders.sectionYourDecision)}</SectionHeading>

                    {decisionMode === "approve" ? (
                      <div className="space-y-3">
                        {/* Approved amount input */}
                        <label className="flex flex-col gap-1 text-xs text-[color:var(--text-muted)]">
                          {t(keys.orders.approvedAmountLabel)}
                          <NumberInput
                            name="approvedAmount"
                            allowDecimal
                            min={0}
                            value={state.approvedAmountInput}
                            onChange={(event) => {
                              state.setApprovedAmountInput(event.target.value);
                              if (showApproveSummary) setShowApproveSummary(false);
                            }}
                            onBlur={() => setApprovedAmountTouched(true)}
                            className={`h-10 rounded-lg border px-3 text-sm text-[color:var(--text-primary)] ${
                              approvedAmountHasError
                                ? "border-[color:var(--error-500)]"
                                : "border-[color:var(--border-light)]"
                            }`}
                            disabled={state.isConflict}
                          />
                          {approvedAmountHasError ? (
                            <span className="text-[color:var(--error-500)]">
                              {t(keys.orders.approvedAmountInvalid)}
                            </span>
                          ) : null}
                        </label>

                        {/* Inline approve summary banner */}
                        {showApproveSummary ? (
                          <div className="rounded-xl border border-[color:var(--primary-200)] bg-[color:var(--primary-50)] px-3 py-3">
                            <p className="text-sm font-medium text-[color:var(--primary-700)]">
                              {t(keys.orders.approveConfirmSummary, {
                                approved: formatAmountValue(locale, state.approvedAmount || 0),
                              })}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Rejection reason */}
                        <div className="flex flex-col gap-1 text-xs text-[color:var(--text-muted)]">
                          {t(keys.orders.reviewRejectReasonLabel)}
                          <Combobox
                            options={[
                              { value: "", label: t(keys.orders.declineReasonPlaceholder) },
                              ...rejectOptions.map(([value, labelKey]) => ({
                                value,
                                label: t(labelKey),
                              })),
                            ]}
                            value={state.reasonCode}
                            onChange={(value) =>
                              state.setReasonCode((value ?? "") as PaymentReviewRejectReasonCode | "")
                            }
                          />
                        </div>

                        {/* Note */}
                        <label className="flex flex-col gap-1 text-xs text-[color:var(--text-muted)]">
                          {t(keys.orders.declineNoteLabel)}
                          <textarea
                            value={state.note}
                            onChange={(event) => state.setNote(event.target.value)}
                            className="min-h-20 rounded-lg border border-[color:var(--border-light)] px-3 py-2 text-sm text-[color:var(--text-primary)]"
                            disabled={state.isConflict}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-5">
            {showApproveSummary ? (
              <>
                <Button
                  type="button"
                  variant="primary"
                  className="h-10 rounded-lg px-4 text-sm font-semibold"
                  onClick={handleConfirmApprove}
                  disabled={state.isSubmitting}
                >
                  {state.isSubmitting ? t(keys.orders.savingAction) : t(keys.orders.confirmApproveTitle)}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-10 rounded-lg px-4 text-sm font-semibold"
                  onClick={() => setShowApproveSummary(false)}
                  disabled={state.isSubmitting}
                >
                  {t(keys.common.back)}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="ms-auto h-10 rounded-lg px-4 text-sm font-semibold"
                  onClick={requestClose}
                  disabled={state.isSubmitting}
                >
                  {t(keys.common.close)}
                </Button>
              </>
            ) : decisionMode === "approve" ? (
              <>
                <Button
                  type="button"
                  variant="primary"
                  className="h-10 rounded-lg px-4 text-sm font-semibold"
                  onClick={handleApprove}
                  disabled={
                    state.isSubmitting ||
                    state.isLoading ||
                    state.isConflict ||
                    !state.hasProof
                  }
                >
                  {t(keys.orders.approvePaymentAction)}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="ms-auto h-10 rounded-lg px-4 text-sm font-semibold"
                  onClick={requestClose}
                  disabled={state.isSubmitting}
                >
                  {t(keys.common.close)}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 rounded-lg border-[color:var(--error-500)]/40 px-4 text-sm font-semibold text-[color:var(--error-500)] hover:bg-[color:var(--error-500)]/5"
                  onClick={handleReject}
                  disabled={state.isSubmitting || state.isLoading || state.isConflict}
                >
                  {t(keys.orders.rejectPaymentAction)}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="ms-auto h-10 rounded-lg px-4 text-sm font-semibold"
                  onClick={requestClose}
                  disabled={state.isSubmitting}
                >
                  {t(keys.common.close)}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showRejectConfirm}
        title={t(keys.orders.confirmRejectTitle)}
        body={t(keys.orders.confirmRejectBody, {
          reason:
            state.reasonCode && rejectReasonLabelByCode[state.reasonCode]
              ? t(rejectReasonLabelByCode[state.reasonCode])
              : t(keys.common.empty),
        })}
        confirmLabel={t(keys.orders.rejectPaymentAction)}
        cancelLabel={t(keys.common.back)}
        isSubmitting={state.isSubmitting}
        onCancel={() => setShowRejectConfirm(false)}
        onConfirm={() => {
          setShowRejectConfirm(false);
          void state.submitReject();
        }}
      />

      <DiscardChangesDialog
        isOpen={showDiscardConfirm}
        onContinue={() => setShowDiscardConfirm(false)}
        onDiscard={() => {
          setShowDiscardConfirm(false);
          onClose();
        }}
        title={t(keys.forms.discardChangesTitle)}
        body={t(keys.forms.discardChangesBody)}
        discardLabel={t(keys.forms.discardChangesDiscard)}
        continueLabel={t(keys.forms.discardChangesContinue)}
      />
    </>
  );
}

// --- Sub-components ---

function SectionHeading({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={`text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)] ${className ?? ""}`}
    >
      {children}
    </p>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}
